import React, { useCallback } from 'react';
import { useHistory } from 'react-router';

import { Collapse, SvgIcon, Tooltip } from '@mui/material';

import { NEW_FEATURE_DATA_ATTRIBUTE } from 'app/constants';
import { useMenuContext } from 'pages/layout/components/MenuContext';

import useStyles from './styles';

interface Props {
    icon?: React.ElementType;
    text?: string;
    link: string;
    itemKey: string;
    compact?: boolean;
    children: React.ReactNode;
    onClick?: () => void;
    [NEW_FEATURE_DATA_ATTRIBUTE]?: string;
}

const MenuItem: React.FC<Props> = ({ icon, text, link, children, itemKey, compact, onClick, ...props }) => {
    const history = useHistory();
    const { classes, cx } = useStyles();
    const { adaptive, active, expanded } = useMenuContext();

    const isExpanded = expanded.includes(itemKey);

    const handleClick = useCallback(
        (event) => {
            event.preventDefault();
            link.startsWith('https') ? window.open(link, '_blank') : history.push(link);
            onClick?.();
        },
        [history, link, onClick]
    );

    return (
        <>
            <Tooltip
                title={compact ? text : null}
                aria-label={''}
                disableInteractive
                placement="right"
                classes={{ tooltip: classes.tooltip }}
            >
                <a
                    href={link}
                    className={cx(classes.link, classes.menuItem, {
                        [classes.active]: itemKey === active,
                        [classes.expanded]: isExpanded
                    })}
                    onClick={handleClick}
                    {...{ [NEW_FEATURE_DATA_ATTRIBUTE]: props[NEW_FEATURE_DATA_ATTRIBUTE] }}
                >
                    <SvgIcon className={classes.icon} component={icon ?? (null as never)} />
                    <div className={cx(classes.text, { [classes.textAdaptive]: adaptive })}>{text}</div>
                </a>
            </Tooltip>
            {children && <Collapse in={isExpanded}>{children}</Collapse>}
        </>
    );
};

export default MenuItem;
